import React, { useEffect, useState } from "react";
import { Typography } from "@mui/material";
import { Layout, SectionContent, SectionHead } from "@layouts/MainLayout";
import { useTranslation } from "react-i18next";
import TableList from "@components/TableList";
import { RowSelectionState } from "@tanstack/react-table";
import { columns } from "@pages/user/units/_components/columns";
import { Unit, UnitList } from "@lib/types/units";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getUnits } from "@lib/api/queries/units";
import ViewUnit from "@pages/user/units/_components/viewUnits";
import PromptDialog from "@components/PromptDialog";
import { useSnackbar } from "@context/SnackBarProvider";
import { AxiosError } from "axios";
import useHandleError from "@lib/utils/errorHook";
import { toggleUnitAuthorization } from "@lib/api/mutations/unit";

const UnitsMain: React.FC = () => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const handleError = useHandleError();

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [unitDetails, setUnitDetails] = useState<Unit | undefined>(undefined);

  const [selectedUnit, setSelectedUnit] = useState<Unit | undefined>(undefined);
  const [showTogglePrompt, setShowTogglePrompt] = useState(false);

  const {
    data: units,
    isLoading,
    isFetching,
    refetch,
    error,
  } = useQuery<UnitList>({
    queryKey: ["unit"],
    queryFn: () => getUnits(),
    refetchOnWindowFocus: false,
  });

  const { mutate: toggleUnitAuthFn, isPending: isDeletingDriver } = useMutation(
    {
      mutationFn: toggleUnitAuthorization,
      onSuccess: (data: any) => {
        showSnackbar(t("user.units.toggleSucces"), "success");

        setSelectedUnit(undefined);
        refetch();
      },
      onError: (error: AxiosError | unknown) => {
        if (error instanceof AxiosError && error.response) {
          const errorDetails = handleError(error.response.data);
          errorDetails && showSnackbar(errorDetails.message, "error");
        } else {
          showSnackbar(t("general.error"), "error");
        }
      },
    }
  );

  const handleView = (unitDetails: Unit) => {
    console.log(unitDetails);
    setUnitDetails(unitDetails);
    setViewDialogOpen(true);
  };

  const confirmAuthorizationToggle = () => {
    if (!selectedUnit) return;
    toggleUnitAuthFn(selectedUnit.unit_id);
    setShowTogglePrompt(false);
  };

  const handleAuthorizationToggle = (unit: Unit) => {
    setSelectedUnit(unit);
    setShowTogglePrompt(true);
  };

  useEffect(() => {
    if (error instanceof AxiosError && error.response) {
      const errorDetails = handleError(error.response.data);
      errorDetails && showSnackbar(errorDetails.message, "error");
    }
  }, [error]);

  return (
    <Layout>
      <SectionHead title={t("user.units.header")} />

      <SectionContent>
        {!error && (
          <TableList
            data={units?.units || []}
            columns={columns(handleView, handleAuthorizationToggle)}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            isLoading={isFetching}
          />
        )}

        <ViewUnit
          viewUnitDialogOpen={viewDialogOpen}
          setViewUnitDialogOpen={setViewDialogOpen}
          unitData={unitDetails}
        />

        <PromptDialog
          handleClose={() => setShowTogglePrompt(false)}
          handleConfirm={() => confirmAuthorizationToggle()}
          isOpen={showTogglePrompt}
          title={t("user.units.toggleAuthorizationStatus")}
          description={
            selectedUnit?.authorization_status === "1"
              ? t("user.units.disablePrompt")
              : t("user.units.enablePrompt")
          }
        />
      </SectionContent>
    </Layout>
  );
};

export default UnitsMain;
